import { z } from "zod";
import { getPasswordRequirements } from "@/libs/password";

import { PROFILE_IMAGE_MAX_BYTES as MAX_FILE_SIZE, PROFILE_IMAGE_TYPES as ACCEPTED_IMAGE_TYPES } from "@/libs/profile-image";

export const sendVerificationSchema = z.object({
  email: z.string().email("A valid email is required."),
  captcha: z.string().trim().min(1, "Captcha is required."),
});

export const signupFormSchema = z
  .object({
    name: z.string().min(2, "Name must be at least 2 characters."),
    sur_name: z.string().min(2, "Surname must be at least 2 characters."),
    email: z.string().email("Please enter a valid email address."),
    password: z.string().superRefine((password, ctx) => {
      getPasswordRequirements(password)
        .filter((requirement) => !requirement.met)
        .forEach((requirement) => {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: requirement.label });
        });
    }),
    repeat_password: z.string(),

    verification_code: z
      .string()
      .length(6, "Verification code must be 6 digits."),
    captcha: z.string().trim().min(1, "Captcha is required."),

    image: z
      .instanceof(File, { message: "Profile image must be a file." })
      .refine((file) => file.size > 0, "Profile image must not be empty.")
      .refine((file) => file.size <= MAX_FILE_SIZE, `Max file size is 3MB.`)
      .refine(
        (file) => ACCEPTED_IMAGE_TYPES.includes(file.type),
        "Only .jpg, .jpeg, .png and .webp formats are supported.",
      )
      .optional(),
  })
  .refine((data) => data.password === data.repeat_password, {
    message: "Passwords do not match.",
    path: ["repeat_password"],
  });

export const signinFormSchema = z.object({
  email: z.string().email("Please enter a valid email address."),
  password: z.string().min(1, "Password is required."),
  captcha: z.string().trim().min(1, "Captcha is required."),
});

export type SignupFormSchema = z.infer<typeof signupFormSchema>;
export type SigninFormSchema = z.infer<typeof signinFormSchema>;
